import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface ScriptContextMenuProps {
  x: number;
  y: number;
  kind: "script" | "folder";
  /** Document library rows (full edit) vs readonly editor-owned scripts. */
  mode?: "document" | "editor";
  onRename?: () => void;
  onNewScript?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onCopyToDocument?: () => void;
  onRun?: () => void;
  onClose: () => void;
}

export function ScriptContextMenu({
  x,
  y,
  kind,
  mode = "document",
  onRename,
  onNewScript,
  onDuplicate,
  onDelete,
  onCopyToDocument,
  onRun,
  onClose,
}: ScriptContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => ({ left: x, top: y }));
  const editorMode = mode === "editor";

  useLayoutEffect(() => {
    function updatePosition(): void {
      const menu = menuRef.current;
      const margin = 8;
      const width = menu?.offsetWidth ?? 160;
      const height = menu?.offsetHeight ?? 120;
      setPosition({
        left: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
        top: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin)),
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [x, y]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close context menu"
        className="fixed inset-0 z-40 cursor-default"
        onClick={onClose}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="fixed z-50 overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 text-[12px] text-slate-200 shadow-xl shadow-black/30"
        style={{ left: position.left, top: position.top, width: "min(11rem, calc(100vw - 16px))" }}
        role="menu"
      >
        {editorMode ? (
          <>
            {kind === "script" && onRun ? (
              <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onRun} role="menuitem">
                Run
              </button>
            ) : null}
            {onCopyToDocument ? (
              <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onCopyToDocument} role="menuitem">
                Copy to document
              </button>
            ) : null}
          </>
        ) : (
          <>
            {onRename ? (
              <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onRename} role="menuitem">
                Rename
              </button>
            ) : null}
            {kind === "folder" && onNewScript ? (
              <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onNewScript} role="menuitem">
                New script
              </button>
            ) : null}
            {kind === "script" && onDuplicate ? (
              <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onDuplicate} role="menuitem">
                Duplicate
              </button>
            ) : null}
            {onDelete ? (
              <button type="button" className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]" onClick={onDelete} role="menuitem">
                Delete
              </button>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
